import { useCallback } from 'react'
import { useAccountType } from '@/hooks/useAccountType'
import { apiClient } from '@/lib/api/api-client'
import { createLogger } from '@/lib/logger'
import { useOrganizationStore } from '@/store/organization.store'
import { useQuery } from '@tanstack/react-query'

import { FEATURE_KEYS } from '@scrambl/shared'

const logger = createLogger('hooks/useSubscriptionFeatures')

interface SubscriptionFeatures {
  [key: string]: boolean | number | string | null | undefined
}

interface UseSubscriptionFeaturesReturn {
  features: SubscriptionFeatures
  isLoading: boolean
  error: Error | null
  checkFeature: (featureKey: string) => boolean
  getFeatureValue: (
    featureKey: string,
  ) => boolean | number | string | null | undefined
  refetch: () => Promise<void>
}

const STALE_TIME_MS = 5 * 60 * 1000 // 5 minutes — matches the previous hand-rolled cache window

/**
 * Hook returning the current org's subscription feature map. Backed by React
 * Query so concurrent first-render callers (~10+ consumers across the app)
 * dedupe to a single in-flight request — previously the hand-rolled Map cache
 * only kicked in *after* a request resolved, so simultaneous boots all fired.
 */
export function useSubscriptionFeatures(): UseSubscriptionFeaturesReturn {
  const currentOrganization = useOrganizationStore(
    state => state.currentOrganization,
  )
  const { isPersonal } = useAccountType()
  const orgId = currentOrganization?.id

  const query = useQuery<SubscriptionFeatures, Error>({
    queryKey: ['subscription-features', orgId],
    queryFn: async () => {
      const response = await apiClient<{ data: SubscriptionFeatures }>(
        `/subscriptions/organizations/${orgId}/features`,
      )
      return response.data || {}
    },
    // PERSONAL accounts have no subscription — skip the fetch entirely.
    enabled: !!orgId && !isPersonal,
    staleTime: STALE_TIME_MS,
    // On error, fall back to all-disabled features so consumers don't crash.
    placeholderData: undefined,
  })

  if (query.error) {
    logger.error('Failed to fetch subscription features:', query.error)
  }

  const features: SubscriptionFeatures =
    query.data ??
    (query.error
      ? Object.values(FEATURE_KEYS).reduce<SubscriptionFeatures>((acc, key) => {
          acc[key] = false
          return acc
        }, {})
      : {})

  const checkFeature = useCallback(
    (featureKey: string): boolean => {
      // PERSONAL accounts: the subscription system is inert (free by
      // construction). Every subscription-gated feature is treated as
      // available; feature flags remain the real gate for personal module
      // visibility.
      if (isPersonal) return true
      const value = features[featureKey]
      if (typeof value === 'boolean') return value
      if (typeof value === 'number') return value > 0
      return false
    },
    [features, isPersonal],
  )

  const getFeatureValue = useCallback(
    (featureKey: string): boolean | number | string | null | undefined => {
      return features[featureKey]
    },
    [features],
  )

  const refetch = useCallback(async () => {
    await query.refetch()
  }, [query])

  return {
    features,
    // During persisted-cache restoration TanStack pauses the initial fetch.
    // `isLoading` is false in that state even though no feature data is ready,
    // so consumers must use the query's pending state before denying access.
    // PERSONAL accounts skip the fetch (query stays pending/idle), so report
    // "not loading" — checkFeature already resolves to true for them.
    isLoading: isPersonal ? false : query.isPending,
    error: query.error ?? null,
    checkFeature,
    getFeatureValue,
    refetch,
  }
}

// Helper hook to check a specific feature
export function useFeature(featureKey: string): {
  isEnabled: boolean
  value: boolean | number | string | null | undefined
  isLoading: boolean
} {
  const { isLoading, checkFeature, getFeatureValue } = useSubscriptionFeatures()

  return {
    isEnabled: checkFeature(featureKey),
    value: getFeatureValue(featureKey),
    isLoading,
  }
}

// Helper hook to check feature limits
export function useFeatureLimit(featureKey: string): {
  limit: number
  isUnlimited: boolean
  hasLimit: boolean
  checkLimit: (current: number) => boolean
  remaining: (current: number) => number
  isLoading: boolean
} {
  const { getFeatureValue, isLoading } = useSubscriptionFeatures()
  const value = getFeatureValue(featureKey)

  // Handle the case where the feature value might be undefined/null vs a valid
  // number (including 0). For undefined/null, fall back to free-tier defaults
  // for known limit features.
  let limit: number

  if (typeof value === 'number') {
    limit = value
  } else {
    if (featureKey === FEATURE_KEYS.MAX_PROJECTS) {
      limit = 2
    } else if (featureKey === FEATURE_KEYS.MAX_USERS) {
      limit = 1
    } else {
      limit = 0
    }

    if (!isLoading && value === undefined) {
      logger.warn(
        `[useFeatureLimit] Feature ${featureKey} has undefined value after loading completed, defaulting to ${limit}`,
      )
    }
  }

  return {
    limit,
    isUnlimited: limit === -1,
    hasLimit: limit > 0 && limit !== -1,
    checkLimit: (current: number) => {
      if (limit === -1) return true
      return current < limit
    },
    remaining: (current: number) =>
      limit === -1 ? -1 : Math.max(0, limit - current),
    isLoading,
  }
}

/**
 * Imperative cache reset, kept for backwards compatibility with code paths
 * that previously called the module-level `featureCache` map directly. New
 * callers should prefer `queryClient.invalidateQueries({ queryKey: ['subscription-features'] })`.
 */
export function clearFeatureCache(_organizationId?: string) {
  // Intentionally a no-op stub: invalidation is now handled by React Query
  // via the org-switch effect in organization.store. Left exported so existing
  // imports still resolve.
}
